import { motion } from 'framer-motion'
import { SectionWrapper, Card } from '../components/primitives/index.js'
import { useProducts } from '../context/ProductsContext.jsx'
import './ScienceStrip.css'

const DEFAULTS = {
  eyebrow: 'Why Researchers Trust Us',
  heading: 'Rigor, at every step.',
  body: 'From synthesis to shipping, every batch is verified and handled to the same exacting standard.',
}

const POINTS = [
  {
    icon: 'flask',
    title: 'Third-party COA on every batch',
    body: 'Every SKU ships with an independent certificate of analysis verifying purity and identity.',
  },
  {
    icon: 'snowflake',
    title: 'Cold-chain from lab to door',
    body: 'Temperature-controlled packaging and expedited shipping keep peptides stable in transit.',
  },
  {
    icon: 'shield',
    title: '99%+ purity, batch-verified',
    body: 'HPLC-verified purity on every production run, not just spot-checked samples.',
  },
]

const ICONS = { flask: FlaskIcon, snowflake: SnowflakeIcon, shield: ShieldIcon }

export default function ScienceStrip() {
  const { siteContent } = useProducts()
  const c = { ...DEFAULTS, ...(siteContent?.science ?? {}) }
  const points = siteContent?.science?.points?.length ? siteContent.science.points : POINTS

  return (
    <SectionWrapper id="science" className="science container">
      <div className="science__header">
        <span className="science__eyebrow">{c.eyebrow}</span>
        <h2>{c.heading}</h2>
        <p>{c.body}</p>
      </div>

      <motion.div
        className="science__grid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-10%' }}
        variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      >
        {points.map((point) => {
          const Icon = ICONS[point.icon] ?? FlaskIcon
          return (
            <motion.div
              key={point.title}
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <Card glow className="science__card">
                <div className="science__icon"><Icon /></div>
                <h3>{point.title}</h3>
                <p>{point.body}</p>
              </Card>
            </motion.div>
          )
        })}
      </motion.div>
    </SectionWrapper>
  )
}

function FlaskIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M9 3h6M10 3v6l-5.5 9.5A2 2 0 0 0 6.2 21h11.6a2 2 0 0 0 1.7-2.5L14 9V3" />
    </svg>
  )
}
function SnowflakeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 2v20M4 7l16 10M20 7 4 17" />
    </svg>
  )
}
function ShieldIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 2 4 5v6c0 5 3.4 8.4 8 11 4.6-2.6 8-6 8-11V5l-8-3Z" />
    </svg>
  )
}
